// store/slices/drawingSlice.ts

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { DrawAOIFeature, DrawAOIGeometry, DrawAOIState, DrawingAOIMode } from "../../plugins/aoi/types";
import
{
  AOI_ACTIVE_FEATURE_COLOR,
  AOI_ACTIVE_FEATURE_STROKE_COLOR,
  AOI_ACTIVE_FEATURE_STROKE_WIDTH,
  AOI_FEATURE_COLOR,
  AOI_FEATURE_KEY,
  AOI_FEATURE_STROKE_COLOR,
  AOI_FEATURE_STROKE_WIDTH,
} from "../../plugins/aoi/consts";

const initialState: DrawAOIState = {
  mode: "NONE",
  features: [],
  activeFeatureId: null, // get active feature by ID
  editingFeatureId: null,
  featureToDelete: null,
  counters: {},
};
const drawSlice = createSlice({
  name: "aoi",
  initialState: initialState,
  reducers: {
    setDrawingAOIMode(state, action: PayloadAction<DrawingAOIMode>)
    {
      state.mode = action.payload;
      if (state.mode !== "EDIT")
      {
        state.editingFeatureId = null;
      }
    },
    setAOIFeatures(state, action: PayloadAction<DrawAOIFeature[]>)
    {

      state.features = action.payload;
    },
    setAOIEditingFeatureId(state, action: PayloadAction<string | null>)
    {
      state.editingFeatureId = action.payload;
    },
    setAOIFeatureToDelete(state, action: PayloadAction<string | null>)
    {
      state.featureToDelete = action.payload;
      state.features = state.features.filter((f) => f.id !== action.payload);
      state.editingFeatureId = null;
    },
    setAOIActiveFeatureId(state, action: PayloadAction<string | null>)
    {
      const newActiveId = action.payload;
      state.activeFeatureId = newActiveId;
      state.features = state.features.map((feature) => ({
        ...feature,
        fill: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
        stroke: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
        strokeWidth: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_STROKE_WIDTH : AOI_FEATURE_STROKE_WIDTH,
        properties: {
          ...feature?.properties,
          color: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
          fill: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
          stroke: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
          strokeWidth: feature.id === newActiveId ? AOI_ACTIVE_FEATURE_STROKE_WIDTH : AOI_FEATURE_STROKE_WIDTH,
        },
      }));
    },

    addAOIFeature(
      state,
      action: PayloadAction<{
        id?: string;
        geometry: DrawAOIGeometry;
        label?: string;
        fill?: string;
        stroke?: string;
        strokeWidth?: number;
        createdAt?: number;
        properties?: Record<string, any>;
      }>,
    )
    {
      const { id, geometry, label, fill, stroke, strokeWidth, createdAt, properties, } = action.payload;
      const newId = id || `${AOI_FEATURE_KEY}-${crypto.randomUUID()}`;

      // ❗ check trùng id
      const isExist = state.features?.some(f => f.id === newId);
      if (isExist)
      {
        return; // hoặc bạn có thể update thay vì return
      }
      const featureType = geometry.type;
      const counterValue = state.counters[featureType] ?? 0;
      state.counters = {
        ...state.counters,
        [featureType]: counterValue + 1,
      };



      const feat: DrawAOIFeature = {
        id: newId,
        geometry,
        label: label?.trim() || `${featureType} ${counterValue + 1}`,
        fill: AOI_ACTIVE_FEATURE_COLOR,
        stroke: AOI_ACTIVE_FEATURE_STROKE_COLOR,
        strokeWidth: AOI_FEATURE_STROKE_WIDTH,
        createdAt: createdAt || Date.now(),
        visible: true,
        properties
      };
      const newFeautes: DrawAOIFeature[] = state.features?.slice().map((feature: DrawAOIFeature) => ({
        ...feature,
        fill: AOI_FEATURE_COLOR,
        stroke: AOI_FEATURE_STROKE_COLOR,
        strokeWidth: AOI_FEATURE_STROKE_WIDTH,
        properties: {
          ...feature?.properties,
          color: AOI_FEATURE_COLOR,
          fill: AOI_FEATURE_COLOR,
          stroke: AOI_FEATURE_STROKE_COLOR,
          strokeWidth: AOI_FEATURE_STROKE_WIDTH,
        },
      }));


      newFeautes.push(feat);
      state.activeFeatureId = newId;
      state.features = newFeautes;


    },
    updateAOIFeature(state, action: PayloadAction<{ id: string; geometry: DrawAOIGeometry }>)
    {
      const { id, geometry } = action.payload;
      const featureIndex = state.features.findIndex((feature) => feature.id === id);

      if (featureIndex !== -1)
      {
        state.features[featureIndex].geometry = geometry;
      }
    },
    removeAOIFeature(state, action: PayloadAction<string>)
    {
      state.featureToDelete = action.payload;
      const idx = state.features.findIndex((feature) => feature.id === action.payload);
      let activeId = state.activeFeatureId;
      if (action.payload === state.activeFeatureId)
      {
        // change active id
        if (idx > 0 && idx < state.features?.length)
        {
          activeId = state.features[idx - 1].id;
        } else if (idx === 0 && state.features?.length > 1)
        {
          activeId = state.features[1].id;
        } else activeId = null;
      }
      const newFeatures = state.features
        .filter((feature) => feature.id !== action.payload)
        ?.slice()
        .map((feature: DrawAOIFeature) => ({
          ...feature,
          fill: feature?.id === activeId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
          stroke: feature?.id === activeId ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
          strokeWidth: feature?.id === activeId ? AOI_ACTIVE_FEATURE_STROKE_WIDTH : AOI_FEATURE_STROKE_WIDTH,
          properties: {
            ...feature?.properties,
            color: feature?.id === activeId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
            fill: feature?.id === activeId ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
            stroke: feature?.id === activeId ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
            strokeWidth: feature?.id === activeId ? AOI_ACTIVE_FEATURE_STROKE_WIDTH : AOI_FEATURE_STROKE_WIDTH,
          },
        }));

      state.activeFeatureId = activeId;
      state.features = newFeatures;
      state.editingFeatureId = null;
    },
    clearAOIFeatures(state)
    {
      state.featureToDelete = null;
      state.features = [];
      state.editingFeatureId = null;
      state.activeFeatureId = null;
    },

    toggleFeatureVisibility(
      state,
      action: PayloadAction<{ id: string }>
    )
    {
      state.features = state.features.map((f) =>
        f.id === action.payload.id
          ? { ...f, visible: !f.visible }
          : f
      );
    },


    showAllAOI(state)
    {
      state.features = state.features.map((f) => ({
        ...f,
        visible: true,
      }));
    },

    setFeatureVisibility(
      state,
      action: PayloadAction<{ id: string; visible: boolean }>
    )
    {
      const { id, visible } = action.payload;

      state.features = state.features.map((f) =>
        f.id === id ? { ...f, visible } : f
      );
    },


    setActiveFeature(
      state,
      action: PayloadAction<{ id: string }>
    )
    {
      const { id } = action.payload;

      state.activeFeatureId = id;

      state.features = state.features.map((feature) =>
      {
        const isActive = feature.id === id;

        return {
          ...feature,
          fill: isActive ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
          stroke: isActive ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
          strokeWidth: AOI_FEATURE_STROKE_WIDTH,
          properties: {
            ...feature?.properties,
            color: isActive ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
            fill: isActive ? AOI_ACTIVE_FEATURE_COLOR : AOI_FEATURE_COLOR,
            stroke: isActive ? AOI_ACTIVE_FEATURE_STROKE_COLOR : AOI_FEATURE_STROKE_COLOR,
            strokeWidth: AOI_FEATURE_STROKE_WIDTH,
          },
        };
      });
    }

  },

});

export const {
  setDrawingAOIMode,
  setAOIFeatures,
  setAOIEditingFeatureId,
  setAOIFeatureToDelete,
  addAOIFeature,
  updateAOIFeature,
  removeAOIFeature,
  clearAOIFeatures,
  toggleFeatureVisibility,
  setActiveFeature,
  showAllAOI,
  setFeatureVisibility,
  setAOIActiveFeatureId
} = drawSlice.actions;
export default drawSlice.reducer;
